import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { CheckboxWithToolTip } from 'components/WithTooltip';
import { FactorStatusValue } from 'constantsProject';
import { useRequest } from 'features/request/useRequest';
import type { ContractOtherCostItemData, ContractSeasonData } from 'gql/graphql';
import { ContractChangeStatusEnum, ProjectMethodologyEnum, SortEnumType } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { useContractConstructionTypes } from 'pages/Project/Common/Contract/hooks/useContractConstructionTypes';
import { Checkbox, cnMixSpace } from 'uikit';
import { destroyAgGridTableFilters, formatNumberToRu, getCheckedIdsArr } from 'utils';
import { getYesNoStatus } from 'utils/getYesNoTableStatus';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { UnitCostWithOtherHeader } from '../../PriceList/PriceListUER/cellRenderers';
import { TYPE_CONST } from '../../WorkOrder/AddOrderOther/types';
import { CostOtherCellRendererComponent } from '../AgreementsAddOther/costOtherCellRendererComponent';
import { uerContractChangeOtherCostRows } from '../AgreementsQueries';

import { getUpdateRowTable } from './useAgreementTableAddOther';

export const useGetAgreementOtherItems = ({
    contractChangeId,
    refreshData,
    disabledByStatus,
    needUpdateCurrentPriceList,
    isDisabledByProjectStatus,
    contractChangeStatus,
    needUpdatePriceListHandler,
    contractSeasons,
    contractId,
}: {
    contractChangeId: number;
    refreshData: () => void;
    disabledByStatus: boolean;
    isDisabledByProjectStatus: boolean;
    needUpdateCurrentPriceList?: boolean;
    contractChangeStatus?: ContractChangeStatusEnum;
    needUpdatePriceListHandler?: (nextState: boolean) => void;
    contractSeasons: ContractSeasonData[];
    contractId: number;
}) => {
    const tableRef = useRef<AgGridReact>();
    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});

    const checkedIdsArr = useMemo(() => getCheckedIdsArr(checkedIds), [checkedIds]);
    const variables = useMemo(
        () => ({
            contractChangeId,
            where: {},
            order: { section: SortEnumType.Desc },
        }),
        [contractChangeId],
    );

    const [agreementOtherData, agreementOtherStatus, , agreementOtherRequest] = useRequest({
        requestId: 'uerContractChangeOtherCostRows',
        query: uerContractChangeOtherCostRows,
        variables,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'uerContractChangeOtherCostRows',
        requestFn: agreementOtherRequest,
        requestVariables: variables,
    });

    useEffect(() => {
        if (getRowParams?.current) {
            getRowParams?.current?.successCallback(
                getUpdateRowTable(agreementOtherData?.uerContractChangeOtherCostRows?.items ?? []),
                getUpdateRowTable(agreementOtherData?.uerContractChangeOtherCostRows?.items ?? []).length,
            );
        }
    }, [
        agreementOtherData?.uerContractChangeOtherCostRows?.items,
        agreementOtherData?.uerContractChangeOtherCostRows?.totalCount,
        getRowParams,
    ]);

    useEffect(() => {
        if (needUpdateCurrentPriceList && needUpdatePriceListHandler) {
            tableRef?.current?.api?.refreshInfiniteCache();
            needUpdatePriceListHandler(false);
        }
    }, [needUpdateCurrentPriceList, needUpdatePriceListHandler, variables]);

    useEffect(() => {
        if (contractChangeStatus !== ContractChangeStatusEnum.Formation) {
            setCheckedIds({});
        }
    }, [contractChangeStatus]);

    const refreshAgreementsOther = useCallback(
        (skipCheckedReset?: boolean) => {
            tableRef?.current?.api?.refreshInfiniteCache();
            refreshData();

            if (!skipCheckedReset) {
                setCheckedIds({});
            }
        },
        [refreshData],
    );

    const [selection, , , setSelection] = useSelection('useGetagreementOtherItems');
    const [pinnedItems, , , setPinned] = usePin('useGetagreementOtherItems');

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const handleChangeCheckbox = useCallback(
        (rowId: any) =>
            ({ checked }: { checked: boolean }) => {
                if (rowId) {
                    setCheckedIds((ids) => ({ ...ids, [rowId]: checked }));
                }
            },
        [],
    );

    const [contractConstructionTypes] = useContractConstructionTypes(contractId, ProjectMethodologyEnum.Uer);

    const contractSeasonsList = useMemo(() => {
        const items = contractSeasons?.reduce<{ [key: string]: number }>(
            (acc, { id, year }: { id: number; year: number }) => {
                acc[id] = year;

                return acc;
            },
            {},
        );

        return { 0: 'Итого', ...items };
    }, [contractSeasons]);

    const CollapseKeys = R.tail(Object.keys(contractSeasonsList));

    const constructionTypesColumns = contractConstructionTypes?.map(({ shortName, id }: any) => ({
        headerName: shortName,
        marryChildren: true,
        children: Object.entries(contractSeasonsList).map(([key, season], index) => ({
            field: `${id}${key}`,
            headerName: `Сезон ${season}`,
            cellClass: 'textRight',
            spanHeaderHeight: true,
            valueFormatter: ({ data }: { data: ContractOtherCostItemData & { type?: string } }) => {
                if (!data || data.type) {
                    return '';
                }

                const keyAsNumber = Number(key);

                const value = data?.seasonLimits?.find(
                    (item: { constructionTypeId: number; seasonId: number }) =>
                        item.constructionTypeId === id && item.seasonId === keyAsNumber,
                )?.cost;

                return value ? formatNumberToRu(value, 2) : '';
            },
            hide: !!index,
            headerComponent: index ? undefined : UnitCostWithOtherHeader,
            headerComponentParams: index
                ? undefined
                : { collapseKeys: ['', ...CollapseKeys], id, displayName: 'Итого' },
            cellRenderer: index ? undefined : CostOtherCellRendererComponent,
            cellRendererParams: index
                ? undefined
                : {
                      constructionTypeId: id,
                      isExpanded: false,
                  },
        })),
    }));

    const columns = useMemo<(ColDef<ContractOtherCostItemData> | ColGroupDef<ContractOtherCostItemData>)[]>(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                width: 56,
                cellClass: 'center-aligned-cell',
                cellRenderer: ({ data }: { data: ContractOtherCostItemData & { type: string } }) => {
                    if (!data || data.type) {
                        return '';
                    }

                    return disabledByStatus ? (
                        <CheckboxWithToolTip
                            checkboxProps={{ checked: false }}
                            tooltipProps={{
                                mode: 'mouseover',
                                content: 'Удаление возможно только в статусе "На формировании"',
                            }}
                            fakeDisabled
                        />
                    ) : (
                        <Checkbox
                            checked={checkedIds[data?.id] || false}
                            onChange={handleChangeCheckbox(data?.id)}
                            className={cnMixSpace({ mT: '3xs' })}
                            disabled={disabledByStatus || isDisabledByProjectStatus}
                        />
                    );
                },
            },
            {
                headerName: 'Наименование прочих затрат',
                field: 'name',
                width: 400,
                flex: 0,
            },
            {
                field: 'calculatingType',
                headerName: 'Способ расчета',
                width: 120,
                valueGetter: ({ data }) =>
                    (data?.calculatingType && TYPE_CONST[data.calculatingType]) ||
                    (data?.calculatingType && `${data?.calculatingTypeValue.toFixed(2)} %`),
            },
            ...constructionTypesColumns,
            {
                field: 'status',
                headerName: 'Статус записи',
                width: 140,
                valueFormatter: ({ data }) => (data ? FactorStatusValue[data.status] : ''),
            },
            {
                headerName: 'Используется',
                children: [
                    {
                        field: 'isIncludedInWorkOrder',
                        headerName: 'НЗ',
                        width: 100,
                        valueFormatter: ({ data }) => (data ? getYesNoStatus(data.isIncludedInWorkOrder) : ''),
                    },
                    {
                        field: 'isIncludedInActKc2',
                        headerName: 'Акт',
                        width: 100,
                        valueFormatter: ({ data }) => (data ? getYesNoStatus(data?.isIncludedInActKc2) : ''),
                    },
                    {
                        field: 'isIncludedInWorkOrderChange',
                        headerName: 'Изм. к НЗ',
                        width: 100,
                        valueFormatter: ({ data }) => (data ? getYesNoStatus(data?.isIncludedInWorkOrderChange) : ''),
                    },
                ],
            },
        ],
        [checkedIds, constructionTypesColumns, disabledByStatus, handleChangeCheckbox, isDisabledByProjectStatus],
    );
    const visibleColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    const columnsForTableSettings = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 6) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    );
    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: columnsForTableSettings,
                incomingColumns: visible,
                setPinned,
                setSelection,
            });
        },
        [columnsForTableSettings, setPinned, setSelection],
    );

    const handleClearChecked = useCallback(() => {
        setCheckedIds({});
    }, []);

    const totalItems = useMemo(
        () => agreementOtherData?.uerContractChangeOtherCostRows?.totalCount ?? 0,
        [agreementOtherData?.uerContractChangeOtherCostRows?.totalCount],
    );

    const fetchedItems = useMemo(() => {
        if (!getRowParams?.current?.endRow || !totalItems) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalItems) {
            return totalItems;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [totalItems, getRowParams?.current?.endRow]);

    const refreshFilters = useCallback(() => {
        if (tableRef) {
            destroyAgGridTableFilters({
                api: tableRef.current?.api,
                columns: [],
            });
        }
    }, []);

    return {
        datasource: dataSource,
        rowCount: fetchedItems,
        totalCount: totalItems,
        columns: visibleColumns,
        columnsForTableSettings,
        isColumnVisible,
        requestStatus: agreementOtherStatus,
        handleTableSettingsConfirm,
        refreshAgreementsOther,
        checkedIdsArr,
        handleClearChecked,
        tableRef,
        refreshFilters,
        contractConstructionTypes,
    };
};
